import { action, computed } from 'mobx'
import { FeatureToggles } from '@/features/feature-toggles'
import type { GetCurrentMemberFn } from '@/features/iam'
import { type Job } from '@/features/jobs/Job'
import { type BookkeepingProgressionStore } from '@/features/jobs/bookkeeping/BookkeepingProgressionStore'
import { type BookkeepingProgressionViewModel } from '@/features/jobs/details/components/BookkeepingProgressionCard/BookkeepingProgressionViewModel'
import { type BookkeepingGrantAccessDialogViewModel } from '@/features/jobs/details/components/BookkeepingGrantAccessDialog/BookkeepingGrantAccessDialogViewModel'
import { type BookkeepingCorrectCycleDialogViewModel } from '@/features/jobs/details/components/BookkeepingCorrectCycleDialog/BookkeepingCorrectCycleDialogViewModel'
import { type BookkeepingSelectCycleDialogViewModel } from '@/features/jobs/details/components/BookkeepingSelectCycleDialog/BookkeepingSelectCycleDialogViewModel'

/**
 * A bookkeeping job's cycles, and the admin actions on them.
 */
export class JobBookkeepingViewModel {
  constructor(
    private readonly _job: Job,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _bookkeepingProgressionStore: BookkeepingProgressionStore,
    public readonly bookkeepingProgressionVm: BookkeepingProgressionViewModel,
    public readonly bookkeepingGrantAccessDialogVm: BookkeepingGrantAccessDialogViewModel,
    public readonly bookkeepingCorrectCycleDialogVm: BookkeepingCorrectCycleDialogViewModel,
    public readonly bookkeepingSelectCycleDialogVm: BookkeepingSelectCycleDialogViewModel,
  ) {}

  @computed
  get isBookkeepingEnabled() {
    return (
      this._job.jobType?.type === 'Bookkeeping' && FeatureToggles.bookkeeping()
    )
  }

  /**
   * Whether the current user manages bookkeeping in the job's workspace.
   */
  @computed
  get canManageBookkeeping() {
    return this._getCurrentMember(this._job.workspace.id).permissions
      .manageBookkeeping
  }

  /**
   * Returns the bookkeeping progression info if it's a bookkeeping job.
   */
  @computed
  get bookkeepingProgressionInfo() {
    return this._bookkeepingProgressionStore.getBookkeepingProgressionInfo(
      this._job.id,
    )
  }

  /**
   * Returns the completed cycles if it's a bookkeeping job.
   */
  @computed
  get completedBookkeepingCycles() {
    return (
      this.bookkeepingProgressionInfo?.cycles.filter(
        (c) => c.status.type === 'Completed',
      ) ?? []
    )
  }

  /**
   * Whether we are able to correct bookkeeping cycles.
   */
  @computed
  get canCorrectBookkeepingCycles() {
    return (
      this.canManageBookkeeping &&
      Boolean(this.completedBookkeepingCycles.length)
    )
  }

  /**
   * Whether we are able to grant bookkeeping access on behalf of the client.
   */
  @computed
  get canGrantBookkeepingAccessAsAdmin() {
    return (
      this.isBookkeepingEnabled &&
      this.canManageBookkeeping &&
      this.bookkeepingProgressionVm.bookkeepingProgressionInfo?.accessStatus ===
        'UnmanagedAccessClientConfirmationRequested'
    )
  }

  @action.bound
  showSelectCycleDialog() {
    this.bookkeepingSelectCycleDialogVm.modal.show()
  }
}
